import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const BTN = "bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all duration-200 shadow-sm active:scale-[0.98]";
const INPUT = "mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Password must be at least 6 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 shadow-lg">
        <div className="flex items-center gap-2.5 mb-8">
          <img src="/va-logo-orange.png" alt="VA Property Care" className="h-10 w-10 object-contain" />
          <span className="font-display text-lg font-bold text-slate-900">VA Property Care</span>
        </div>
        {done ? (
          <div className="text-center py-6" data-testid="reset-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h1 className="mt-4 text-xl font-semibold text-slate-900">Password updated</h1>
            <p className="text-sm text-slate-500 mt-2">Redirecting you to login…</p>
          </div>
        ) : !token ? (
          <div className="text-center py-6">
            <h1 className="text-xl font-semibold text-slate-900">Invalid link</h1>
            <p className="text-sm text-slate-500 mt-2">This reset link is missing a token.</p>
            <Link to="/login" className="mt-4 inline-block text-emerald-700 font-medium">Back to login</Link>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Set a new password</h1>
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label className="text-sm text-slate-600">New password</label>
                <input required type="password" data-testid="reset-password-input" value={password}
                  onChange={(e) => setPassword(e.target.value)} className={INPUT} />
              </div>
              <div>
                <label className="text-sm text-slate-600">Confirm password</label>
                <input required type="password" data-testid="reset-confirm-input" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} className={INPUT} />
              </div>
              {error && <p className="text-sm text-rose-600">{error}</p>}
              <button type="submit" disabled={busy} data-testid="reset-submit-button"
                className={`${BTN} w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg disabled:opacity-60`}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update Password
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
